// OWMap BSL1 — H5 mobile shell (Phase 2: Canvas globe + live clock + async stories)
// Renders as a floating phone frame overlay for design preview & real H5 share landing.

const { useState: useStateH5, useEffect: useEffectH5, useRef: useRefH5 } = React;

// ── H5 live clock (Beijing time) ────────────────────────────────────────────
function useH5Clock() {
  const [time, setTime] = useStateH5('--:--');
  useEffectH5(() => {
    function update() {
      const now = new Date();
      const bj = new Date(now.getTime() + 8 * 3600 * 1000);
      setTime(
        String(bj.getUTCHours()).padStart(2, '0') + ':' +
        String(bj.getUTCMinutes()).padStart(2, '0')
      );
    }
    update();
    const t = setInterval(update, 30000);
    return () => clearInterval(t);
  }, []);
  return time;
}

// ── H5 Story mini-card ──────────────────────────────────────────────────────
function H5StoryCard({ coord, story, onPlay, accentColor }) {
  if (!story) return null;
  return (
    <div className="h5-card">
      <div className="h5-card-img" style={{ background: `radial-gradient(circle at 40% 50%, ${accentColor}44, transparent 70%)` }}>
        <span className="h5-card-tag" style={{ borderColor: accentColor, color: accentColor }}>
          {(window.CULTURES || {})[coord.culture]?.label || coord.culture}
        </span>
        <span className="h5-card-heat">
          {'★'.repeat(Math.round(coord.heat * 5))}{'☆'.repeat(5 - Math.round(coord.heat * 5))}
        </span>
      </div>
      <div className="h5-card-body">
        <div className="h5-card-coord">{coord.name} · {coord.region}</div>
        <div className="h5-card-title">「{story.title}」</div>
        <div className="h5-card-excerpt">{story.excerpt}</div>
        <div className="h5-card-foot">
          <span className="h5-card-meta">{story.duration} · {story.genre}</span>
          <button className="h5-card-play" onClick={() => onPlay && onPlay(story, coord)}
            style={{ background: accentColor }}>
            <svg viewBox="0 0 24 24" width="11" height="11"><path d="M8 6 L18 12 L8 18 Z" fill="#fff"/></svg>
            播放
          </button>
        </div>
      </div>
    </div>
  );
}

// ── H5 Recommendations list ─────────────────────────────────────────────────
function H5RecList({ selectedCoord, onPlay }) {
  const [items, setItems] = useStateH5([]);
  const [loading, setLoading] = useStateH5(true);

  useEffectH5(() => {
    setLoading(true);
    // Try async API; fall back to seed data immediately
    const seedItems = [
      COORDS.find(c => c.id === 'reykjavik'),
      COORDS.find(c => c.id === 'petra'),
      COORDS.find(c => c.id === 'varanasi'),
      COORDS.find(c => c.id === 'timbuktu'),
    ].filter(Boolean);

    // Show seed data immediately
    setItems(seedItems);
    setLoading(false);

    // Then try to refresh from API
    BSL1API.getRecommendations(4)
      .then(data => {
        const arr = Array.isArray(data) ? data : (data.recommendations || data.coords || []);
        if (arr.length > 0) setItems(arr.slice(0, 4));
      })
      .catch(() => {});
  }, []);

  if (loading) {
    return (
      <div className="h5-mini-list">
        {[1,2,3].map(i => (
          <div key={i} className="h5-mini-row h5-mini-sk">
            <div className="h5-sk-dot"></div>
            <div className="h5-sk-lines">
              <div className="h5-sk-line h5-sk-t"></div>
              <div className="h5-sk-line h5-sk-m"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="h5-mini-list">
      {items.map(c => {
        if (!c) return null;
        const s = getStories(c.id)[0];
        if (!s) return null;
        const color = (window.CULTURES || {})[c.culture]?.color || '#F5C842';
        return (
          <button key={c.id} className={`h5-mini-row ${selectedCoord?.id === c.id ? 'h5-mini-active' : ''}`}
            onClick={() => onPlay && onPlay(s, c)}>
            <span className="h5-mini-dot" style={{ background: color, boxShadow: `0 0 8px ${color}` }}></span>
            <div className="h5-mini-text">
              <div className="h5-mini-title">{s.title}</div>
              <div className="h5-mini-meta">{c.name} · {s.duration}</div>
            </div>
            <svg className="h5-mini-play" viewBox="0 0 24 24" width="12" height="12">
              <path d="M8 6 L18 12 L8 18 Z" fill="currentColor"/>
            </svg>
          </button>
        );
      })}
    </div>
  );
}

// ── H5 Shell ────────────────────────────────────────────────────────────────
function H5Shell({ onClose, onPlay: externalOnPlay }) {
  const [tab, setTab]                   = useStateH5('globe');
  const [selectedCoord, setSelectedCoord] = useStateH5(null);
  const [drawerExpanded, setDrawerExpanded] = useStateH5(false);
  const [currentStory, setCurrentStory] = useStateH5(null);
  const [cultureFilter, setCultureFilter] = useStateH5([]);
  const [showFilter, setShowFilter]     = useStateH5(false);
  const clockTime = useH5Clock();

  const allCoords = window.ALL_COORDS || COORDS;
  const featured = selectedCoord || COORDS.find(c => c.id === 'dunhuang');
  const featuredStory = featured ? getStories(featured.id)[0] : null;
  const accentColor = featured
    ? ((window.CULTURES || {})[featured.culture]?.color || '#E87A3C')
    : '#E87A3C';

  const handleSelectCoord = (coord) => {
    setSelectedCoord(coord);
    setDrawerExpanded(true);
  };

  const handlePlay = (story, coord) => {
    setCurrentStory(story);
    setSelectedCoord(coord);
    // Bubble up to parent app if provided
    if (externalOnPlay) externalOnPlay(story, coord);
  };

  // Filter culture chips
  const cultures = Object.values(window.CULTURES || CULTURES);
  const toggleCulture = (id) => {
    setCultureFilter(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  return (
    <div className="h5-shell">
      <div className="h5-frame">

        {/* Status bar */}
        <div className="h5-statusbar">
          <span className="h5-time">{clockTime}</span>
          <span className="h5-notch"></span>
          <span className="h5-status-icons">
            <svg viewBox="0 0 18 12" width="14" height="9">
              <path d="M1 11 L1 9 M5 11 L5 7 M9 11 L9 5 M13 11 L13 3 M17 11 L17 1" stroke="#fff" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
            <svg viewBox="0 0 18 12" width="14" height="9">
              <path d="M9 9.5 a3 3 0 0 1 0 0 M9 7 a5 5 0 0 1 6 0 M9 4.5 a8 8 0 0 1 8 0" fill="none" stroke="#fff" strokeWidth="1" opacity="0.85"/>
            </svg>
            <span className="h5-batt"><span className="h5-batt-inner"></span></span>
          </span>
        </div>

        {/* Top nav */}
        <div className="h5-topnav">
          <div className="h5-brand">
            <svg viewBox="0 0 32 32" width="18" height="18">
              <circle cx="16" cy="16" r="11" fill="none" stroke="#F5C842" strokeWidth="1.2"/>
              <circle cx="16" cy="16" r="3" fill="#E87A3C"/>
            </svg>
            <span>OWMap</span>
          </div>
          <button className="h5-login">登录</button>
        </div>

        {/* Globe area */}
        <div className="h5-globe-area">
          <div className="h5-globe">
            <H5GlobeMini
              coords={allCoords}
              selectedId={selectedCoord?.id || null}
              onSelectCoord={handleSelectCoord}
              cultureFilter={cultureFilter}
              size={280}
            />
          </div>

          {/* Culture filter chips (compact, above hint) */}
          {showFilter && (
            <div className="h5-culture-chips">
              {cultures.map(c => (
                <button key={c.id}
                  className={`h5-chip ${cultureFilter.length === 0 || cultureFilter.includes(c.id) ? 'h5-chip-on' : 'h5-chip-off'}`}
                  style={{ '--chip-color': c.color }}
                  onClick={() => toggleCulture(c.id)}>
                  <span className="h5-chip-dot" style={{ background: c.color }}></span>
                  {c.label}
                </button>
              ))}
              {cultureFilter.length > 0 && (
                <button className="h5-chip h5-chip-clear" onClick={() => setCultureFilter([])}>全部</button>
              )}
            </div>
          )}

          <div className="h5-globe-actions">
            <button className="h5-action-btn" onClick={() => setShowFilter(!showFilter)} title="文化圈筛选">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path d="M4 6 H20 M7 12 H17 M10 18 H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
            {selectedCoord && (
              <div className="h5-selected-hint">
                <span className="h5-sel-dot" style={{ background: accentColor }}></span>
                <span className="h5-sel-name">{selectedCoord.name}</span>
              </div>
            )}
          </div>
          <div className="h5-hint">{selectedCoord ? `已选中 · ${selectedCoord.name}` : '拖动地球 · 点击光点进入故事'}</div>
        </div>

        {/* Sliding drawer */}
        <div className={`h5-drawer ${drawerExpanded ? 'h5-drawer-on' : ''}`}>
          <button className="h5-drawer-handle" onClick={() => setDrawerExpanded(!drawerExpanded)}>
            <span className="h5-drawer-pill"></span>
          </button>

          {tab === 'globe' && (
            <>
              <div className="h5-drawer-head">
                <div>
                  <div className="h5-drawer-title">
                    {selectedCoord ? selectedCoord.name : '今夜推荐'}
                  </div>
                  <div className="h5-drawer-sub">
                    {selectedCoord ? selectedCoord.region : '基于你的睡前时间'}
                  </div>
                </div>
                {selectedCoord && (
                  <button className="h5-back-btn" onClick={() => setSelectedCoord(null)}>
                    <svg viewBox="0 0 24 24" width="12" height="12">
                      <path d="M15 6 L9 12 L15 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    返回
                  </button>
                )}
              </div>

              {featured && featuredStory && (
                <H5StoryCard coord={featured} story={featuredStory} onPlay={handlePlay} accentColor={accentColor} />
              )}

              <H5RecList selectedCoord={selectedCoord} onPlay={handlePlay} />
            </>
          )}

          {tab === 'lib' && (
            <div className="h5-tab-content">
              <div className="h5-empty-state">
                <div className="h5-empty-icon">◎</div>
                <div className="h5-empty-title">收藏夹为空</div>
                <div className="h5-empty-sub">点击地球上的光点，收藏你喜欢的故事</div>
              </div>
            </div>
          )}

          {tab === 'me' && (
            <div className="h5-tab-content">
              <div className="h5-profile">
                <div className="h5-avatar">
                  <svg viewBox="0 0 48 48" width="48" height="48">
                    <circle cx="24" cy="24" r="22" fill="rgba(245,200,66,0.1)" stroke="rgba(245,200,66,0.3)" strokeWidth="1"/>
                    <circle cx="24" cy="20" r="7" fill="none" stroke="rgba(245,200,66,0.6)" strokeWidth="1.4"/>
                    <path d="M8 42 a16 16 0 0 1 32 0" fill="none" stroke="rgba(245,200,66,0.6)" strokeWidth="1.4"/>
                  </svg>
                </div>
                <div className="h5-profile-info">
                  <div className="h5-profile-name">探索者</div>
                  <div className="h5-profile-sub">Free · 已收听 0 分钟</div>
                </div>
                <button className="h5-upgrade-btn" style={{ background: '#E87A3C' }}>升级会员</button>
              </div>
            </div>
          )}
        </div>

        {/* Now playing mini bar */}
        {currentStory && (
          <div className="h5-now-playing" style={{ borderTopColor: accentColor }}>
            <span className="h5-np-dot" style={{ background: accentColor }}></span>
            <span className="h5-np-title">{currentStory.title}</span>
            <button className="h5-np-play" style={{ color: accentColor }}>
              <svg viewBox="0 0 24 24" width="14" height="14">
                <rect x="6" y="5" width="4" height="14" fill="currentColor"/>
                <rect x="14" y="5" width="4" height="14" fill="currentColor"/>
              </svg>
            </button>
          </div>
        )}

        {/* CTA download banner */}
        <div className="h5-cta-banner">
          <div className="h5-cta-text">
            <div className="h5-cta-title">下载 App，完整体验</div>
            <div className="h5-cta-sub">5,420 个坐标 · 离线收听 · 摇一摇随机</div>
          </div>
          <button className="h5-cta-btn">下载</button>
        </div>

        {/* Tab bar */}
        <div className="h5-tabbar">
          <button className={`h5-tab ${tab === 'globe' ? 'h5-tab-on' : ''}`} onClick={() => setTab('globe')}>
            <svg viewBox="0 0 24 24" width="18" height="18">
              <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.4"/>
              <ellipse cx="12" cy="12" rx="9" ry="3.5" fill="none" stroke="currentColor" strokeWidth="1.2"/>
              <path d="M12 3 V21" stroke="currentColor" strokeWidth="1.2"/>
            </svg>
            <span>地球</span>
          </button>
          <button className={`h5-tab ${tab === 'lib' ? 'h5-tab-on' : ''}`} onClick={() => setTab('lib')}>
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path d="M7 4 L17 4 L17 21 L12 17 L7 21 Z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
            </svg>
            <span>收藏</span>
          </button>
          <button className={`h5-tab ${tab === 'me' ? 'h5-tab-on' : ''}`} onClick={() => setTab('me')}>
            <svg viewBox="0 0 24 24" width="18" height="18">
              <circle cx="12" cy="9" r="3.5" fill="none" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M5 20 a7 7 0 0 1 14 0" fill="none" stroke="currentColor" strokeWidth="1.4"/>
            </svg>
            <span>我的</span>
          </button>
        </div>

        <div className="h5-home-bar"></div>
      </div>

      {/* Close & label */}
      <button className="h5-close" onClick={onClose} title="关闭 H5 预览">
        <svg viewBox="0 0 24 24" width="14" height="14">
          <path d="M6 6 L18 18 M6 18 L18 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
        </svg>
      </button>
      <div className="h5-label">
        <div className="h5-label-eyebrow">移动端 · H5</div>
        <div className="h5-label-title">独立使用模式</div>
        <div className="h5-label-sub">375 × 812 · 分享着陆页与浏览器内体验</div>
      </div>
    </div>
  );
}

window.H5Shell = H5Shell;
